import { getCardImageUri } from '@mtg-deck-tool/shared';
import type { CardInstance } from '@mtg-deck-tool/shared';
import { usePlaytesterStore } from '../../stores/playtester-store.js';
import { CARD_CORNER_RADIUS } from '../../lib/playtester-engine.js';

interface CommanderCardProps {
  instance: CardInstance;
}

function CommanderCard({ instance }: CommanderCardProps) {
  const castCommander = usePlaytesterStore((s) => s.castCommander);
  const { card, castCount } = instance;
  // Use 'large' (672×936) for crisp rendering at high zoom; falls back to 'normal'.
  const artUri = getCardImageUri(card, 'large');
  const taxCost = castCount * 2;

  return (
    <div className="flex flex-col items-center gap-0.5">
      {/* Card tile — same size as Library / GY / Exile piles */}
      <button
        onClick={() => castCommander(instance.instanceId)}
        className="relative group shrink-0 rounded-sm overflow-hidden border border-gray-700 hover:border-orange-500 transition"
        style={{ width: 'var(--pt-card-w)', height: 'var(--pt-card-h)' }}
        title={`Cast ${card.name}${taxCost > 0 ? ` (+{${taxCost}} commander tax)` : ''}`}
      >
        {artUri ? (
          <img
            src={artUri}
            alt={card.name}
            className="w-full h-full object-cover"
            draggable={false} style={{ borderRadius: CARD_CORNER_RADIUS }} />
        ) : (
          <div className="w-full h-full bg-gray-800 flex items-center justify-center">
            <span className="text-gray-600 text-[8px] text-center leading-tight px-0.5">
              {card.name}
            </span>
          </div>
        )}

        {/* Cast overlay on hover */}
        <div className="absolute inset-0 bg-orange-600/0 group-hover:bg-orange-600/30 transition flex items-center justify-center">
          <span className="text-white text-[10px] font-bold opacity-0 group-hover:opacity-100 transition drop-shadow">
            Cast
          </span>
        </div>

        {/* Tax badge */}
        {castCount > 0 && (
          <div className="absolute top-0.5 right-0.5 bg-orange-700/90 text-white text-[8px] font-bold px-1 rounded-sm leading-tight">
            +{'{' + taxCost + '}'}
          </div>
        )}

        {/* Label at bottom */}
        <div className="absolute bottom-0.5 inset-x-0 text-center pointer-events-none">
          <span className="text-[8px] text-white/60 font-medium drop-shadow">CMD</span>
        </div>
      </button>

      {/* Cast count below card */}
      {castCount > 0 && (
        <div className="text-[8px] text-orange-400 font-medium leading-none">
          ×{castCount}
        </div>
      )}
    </div>
  );
}

export default function CommandZoneDisplay() {
  const command = usePlaytesterStore((s) => s.command);

  if (command.length === 0) return null;

  return (
    <div className="flex items-end gap-1.5 shrink-0">
      {command.map((instance) => (
        <CommanderCard key={instance.instanceId} instance={instance} />
      ))}
    </div>
  );
}
